/* eslint-disable max-lines-per-function */
import { toast } from '@/hooks/use-toast';
import { faTrashCan } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Drawer,
  DrawerContent,
  DrawerTrigger,
} from '@e2n/ui';
import { Bewertung } from '@/domain/mitarbeiter/features/rotationsplan/entities/Bewertung';
import { useDeleteBewertung } from '@/domain/mitarbeiter/features/rotationsplan/hooks/useDeleteBewertung/useDeleteBewertung';

export function DeleteBewertung({ bewertung }: { bewertung: Bewertung }) {
  const { id } = useParams();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [deleteBewertung] = useDeleteBewertung();
  const { t } = useTranslation();
  const navigate = useNavigate();

  async function handleDelete() {
    try {
      setIsSubmitting(true);
      const result = await deleteBewertung({
        id: bewertung.id,
      });

      if (
        result.error ||
        result.data.mitarbeiter_deleteRotationsplanBewertung.error
      ) {
        toast({
          description: t(
            'domain.mitarbeiter.rotationsplan.rotationsplaene.deleteBewertung.error',
          ),
        });
      } else {
        navigate(`/mitarbeiter/rotationsplan/${id}`);

        toast({
          description: t(
            'domain.mitarbeiter.rotationsplan.rotationsplaene.deleteBewertung.success',
          ),
        });
      }
    } catch (_err) {
      toast({
        description: t(
          'domain.mitarbeiter.rotationsplan.rotationsplaene.deleteBewertung.error',
        ),
      });
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <>
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="flex-col hidden gap-6 p-8 md:max-w-111 md:flex">
          <DialogTitle>
            {t(
              'domain.mitarbeiter.rotationsplan.rotationsplaene.deleteBewertung.title',
            )}
          </DialogTitle>
          <div className="text-sm text-neutrals-900">
            {t(
              'domain.mitarbeiter.rotationsplan.rotationsplaene.deleteBewertung.subtitle',
            )}
          </div>
          <div className="flex items-center justify-end gap-4">
            <Button
              onClick={() => {
                setIsDialogOpen(false);
              }}
              variant="outline">
              {t(
                'domain.mitarbeiter.rotationsplan.rotationsplaene.deleteBewertung.dismiss',
              )}
            </Button>
            <Button
              disabled={isSubmitting}
              variant="destructive"
              onClick={handleDelete}>
              {t(
                'domain.mitarbeiter.rotationsplan.rotationsplaene.deleteBewertung.confirm',
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <div className="hidden md:flex">
        <Button
          disabled={isSubmitting}
          onClick={() => setIsDialogOpen(true)}
          variant="ghost-destructive"
          size="icon">
          <FontAwesomeIcon icon={faTrashCan} />
        </Button>
      </div>

      <Drawer open={isDrawerOpen} onOpenChange={setIsDrawerOpen}>
        <DrawerTrigger className="flex md:hidden" asChild>
          <Button
            onClick={() => setIsDrawerOpen(true)}
            disabled={isSubmitting}
            variant="ghost-destructive"
            size="icon">
            <FontAwesomeIcon icon={faTrashCan} />
          </Button>
        </DrawerTrigger>
        <DrawerContent className="flex flex-col items-center gap-5 p-4 md:hidden">
          <div className="flex flex-col items-center gap-2 text-sm text-center">
            <div className="font-semibold">
              {t(
                'domain.mitarbeiter.rotationsplan.rotationsplaene.deleteBewertung.title',
              )}
            </div>
            <div>
              {t(
                'domain.mitarbeiter.rotationsplan.rotationsplaene.deleteBewertung.subtitle',
              )}
            </div>
          </div>
          <div className="flex flex-col items-center w-full gap-3">
            <Button
              className="w-full"
              onClick={() => {
                setIsDrawerOpen(false);
              }}
              variant="outline">
              {t(
                'domain.mitarbeiter.rotationsplan.rotationsplaene.deleteBewertung.dismiss',
              )}
            </Button>
            <Button
              className="w-full"
              variant="destructive"
              disabled={isSubmitting}
              onClick={handleDelete}>
              {t(
                'domain.mitarbeiter.rotationsplan.rotationsplaene.deleteBewertung.confirm',
              )}
            </Button>
          </div>
        </DrawerContent>
      </Drawer>
    </>
  );
}
